import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import Moment from 'moment';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import AddIcon from '@mui/icons-material/Add';

import Searchbar from '../../../components/searchbar';

import { pointlessPink, purple } from '../../../data/colors';
import IncidentService from '../../../data/incident-service';
import DataContext from '../../../data/data-context';
import Auth from '../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    card: {
        maxWidth: 345,
    },
    name: {
        fontSize: '24px',
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main,
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    assignee: {
        fontSize: '16px',
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    status: {
        fontSize: '16px',
        marginBottom: theme.spacing(1),
        color: theme.palette.secondary.main,
        '& span': {
            '&.new': {
                color: pointlessPink[200],
            },
            '&.open': {
                color: theme.palette.primary.main,
            },
            '&.resolved': {
                color: purple[300],
            },
        }
    },
    mitre: {
        fontSize: '16px',
        marginBottom: theme.spacing(2),
        color: theme.palette.secondary.main,
        '& span': {
            color: purple[300],
        },
    },
    severity: {
        fontSize: '16px',
        marginBottom: theme.spacing(1),
        color: theme.palette.secondary.main,
        '& span': {
            color: theme.palette.error.main,
        },
    },
    tagged: {
        color: theme.palette.primary.main,
    },
    highlight: {
        fontSize: '16px',
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main,
    },
    select: {
        width: '80px',
    },
}));

const IncidentResponse = ({ perm }) => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const [incidents, setIncidents] = useState([]);
    const { mitreCategoriesMap } = useContext(DataContext);
    const [search, setSearch] = useState('');
    const [severity, setSeverity] = useState('all');
    const [status, setStatus] = useState(['n', 'o', 'r']);
    const incidentPerms = Auth.permissions.get('Incidents');

    useEffect(() => {
        getIncidents();
    }, []);

    const getIncidents = () => {
        IncidentService.getAll().then(res => {
            setIncidents(res);
        }, err => {
            console.error(err);
            toast.error(err);
        });
    };

    const handleCreateIncident = () => {
        navigate('/views/incident_response/create');
    };

    const handleOpenIncident = (uuid) => {
        navigate(`/views/incident_response/${uuid}`);
    };

    const handleSearch = ({ search }) => {
        setSearch(search);
    };

    const handleUpdateSeverity = (e) => {
        setSeverity(e.target.value);
    };

    const handleUpdateStatus = (s) => {
        const v = [...status];
        if (v.includes(s)) {
            v.splice(v.indexOf(s), 1);
        } else {
            v.push(s);
        }
        setStatus(v);
    };

    return (
        <Box mb={5}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                        <Box display="flex">
                            <Box mt={1}><Searchbar
                                immediate
                                searchservice={IncidentService}
                                value={search}
                                onSearch={handleSearch}
                            /></Box>
                            <Box ml={2} mt={-2} display="block">
                                <InputLabel id="severity-label">Severity</InputLabel>
                                <Select variant="standard" labelId="severity-label" className={classes.select} value={severity} onChange={handleUpdateSeverity}>
                                    <MenuItem value={'all'}>All</MenuItem>
                                    <MenuItem value={'1'}>1</MenuItem>
                                    <MenuItem value={'2'}>2</MenuItem>
                                    <MenuItem value={'3'}>3</MenuItem>
                                    <MenuItem value={'4'}>4</MenuItem>
                                </Select>
                            </Box>
                            <Box ml={2} mt={1}>
                                <ButtonGroup>
                                    <Button variant={status.includes('n') ? 'contained' : 'outlined'} color="primary" onClick={() => { handleUpdateStatus('n') }}>New</Button>
                                    <Button variant={status.includes('o') ? 'contained' : 'outlined'} color="primary" onClick={() => { handleUpdateStatus('o') }}>Open</Button>
                                    <Button variant={status.includes('r') ? 'contained' : 'outlined'} color="primary" onClick={() => { handleUpdateStatus('r') }}>Resolved</Button>
                                </ButtonGroup>
                            </Box>
                        </Box>
                        <Box mt={1} display={!incidentPerms.write ? 'none' : 'block'}>
                            <Button variant="contained" color="primary" onClick={handleCreateIncident}><AddIcon /> New Incident</Button>
                        </Box>
                    </Box>
                </Grid>
                {incidents.filter(v => {
                    return Object.keys(v).reduce((a, c) => {
                        if (a) {
                            return true;
                        } else if ((c === 'created' || c === 'start') && Moment(v[c]).format(theme.date.long).includes(search)) {
                            return true;
                        } else if (typeof v[c] === 'string' && v[c].toLowerCase().includes(search.toLowerCase())) {
                            return true;
                        }
                        return false;
                    }, false);
                }).filter(v => {
                    if (severity !== 'all') {
                        return parseInt(severity) === v.data.severity;
                    }
                    return true;
                }).filter(v => {
                    if (status.length) {
                        switch (v.status) {
                            case 'new':
                                return status.includes('n');
                            case 'open':
                                return status.includes('o');
                            case 'resolved':
                                return status.includes('r');
                        }
                    }
                    return true;
                }).map((v, i) => {
                    return (
                        <Grid key={'incident-' + i} item xs={12}>
                            <Paper elevation={3}>
                                <Box p={2}>
                                    <Grid container>
                                        <Grid item xs={2}>
                                            <Box className={classes.name} onClick={() => { handleOpenIncident(v.uuid) }}>{v.name}</Box>
                                            <Box className={classes.assignee}>Assignee: <span>{v.assigner}</span></Box>
                                            <Box className={classes.status}>Status: <span className={v.status.toLowerCase()}>{v.status}</span></Box>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Box className={classes.mitre}>
                                                Mitre ATT&CK: <span>
                                                    {mitreCategoriesMap[v.mitre_category_id]
                                                        ? mitreCategoriesMap[v.mitre_category_id].name
                                                        : v.mitre_category_id}
                                                </span></Box>
                                            <Box className={classes.highlight}>Date Created: {Moment(v.created).format(theme.date.long)}</Box>
                                            <Box className={classes.severity}>Severity: <span>{v.data.severity}</span></Box>
                                        </Grid>
                                        {/* <Grid item xs={5}>
                                            <Box className={classes.highlight}>Start: {Moment(v.start).format(theme.date.long)}</Box>
                                            <Box className={classes.highlight}>End: {v.end ? Moment(v.end).format(theme.date.long) : ''}</Box>
                                                    {(() => {
                                                        const i = mitreCategories.findIndex(x => x.id === v.mitre_category_id);
                                                        if (i !== -1) {
                                                            return mitreCategories[i].name;
                                                        }
                                                        return null;
                                                    })()}
                                                </span></Box>
                                            <Box className={classes.highlight}>Date Created: {Moment(v.created).format(theme.date.long)}</Box>
                                            <Box className={classes.severity}>Severity: <span>{v.data.severity}</span></Box>
                                        </Grid> */}
                                        <Grid item xs={5}>
                                            <Box className={classes.highlight}>Start: {Moment(v.start).format(theme.date.long)}</Box>
                                            {v.resolved ? <Box className={classes.highlight}>End: {Moment(v.resolved).format(theme.date.long)}</Box> : null}
                                            {/* <Box className={classes.tagged}>Tagged: </Box> */}
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

IncidentResponse.propTypes = {
    perm: PropTypes.func,
};

export default IncidentResponse;
